import { useMemo, useState } from 'react';
import {
  Activity,
  Bot,
  Boxes,
  Cloud,
  Database,
  ExternalLink,
  FileText,
  GitBranch,
  LayoutDashboard,
  Map,
  Palette,
  Server,
  Settings,
  Shield,
  Terminal,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Button, Chip } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading, Page, Tabs, useAsync, useNotice } from '../components/common';
import type { LinkEnvironment, LinkedTool } from '../api';
import { openTool } from '../lib/openTool';
import { api } from '../session';

/** The icons a link may name (`icon`), normalised: lower case without dashes/underscores. */
const ICONS: Record<string, LucideIcon> = {
  activity: Activity,
  bot: Bot,
  boxes: Boxes,
  cloud: Cloud,
  database: Database,
  filetext: FileText,
  gitbranch: GitBranch,
  layoutdashboard: LayoutDashboard,
  map: Map,
  palette: Palette,
  server: Server,
  settings: Settings,
  shield: Shield,
  terminal: Terminal,
  workflow: Workflow,
  wrench: Wrench,
};

/** The curated icon names offered by the Links editor (kebab case, as stored). */
export const ICON_CHOICES = [
  'activity',
  'bot',
  'boxes',
  'cloud',
  'database',
  'file-text',
  'git-branch',
  'layout-dashboard',
  'map',
  'palette',
  'server',
  'settings',
  'shield',
  'terminal',
  'workflow',
  'wrench',
] as const;

export const iconFor = (name?: string): LucideIcon =>
  (name && ICONS[name.toLowerCase().replace(/[-_\s]/g, '')]) || ExternalLink;

const ENVIRONMENTS: { value: LinkEnvironment; label: string }[] = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
];

function ToolCard({ tool, onOpen, busy }: { tool: LinkedTool; onOpen: () => void; busy: boolean }) {
  const Icon = iconFor(tool.icon);
  return (
    <div className="nyk-card oph-tool" data-tool={tool.id}>
      <div className="oph-tool-head">
        <span className="oph-tool-icon" aria-hidden="true">
          <Icon size={20} />
        </span>
        <h2 className="oph-tool-name">{tool.name}</h2>
        {tool.launch && (
          <Chip size="sm" className="oph-tool-sso" title="Opens signed in (pass-through sign-in)">
            SSO
          </Chip>
        )}
      </div>
      {tool.description && <p className="oph-tool-desc">{tool.description}</p>}
      <div className="oph-tool-foot">
        <span className="nyk-url oph-tool-url" title={tool.url}>
          {tool.url.replace(/^https?:\/\//, '')}
        </span>
        <Button
          size="sm"
          variant="secondary"
          onClick={onOpen}
          loading={busy}
          icon={<ExternalLink size={14} />}
        >
          Open
        </Button>
      </div>
    </div>
  );
}

export function LinkedToolsPage() {
  const { data, loading, error } = useAsync(() => api.links(), []);
  const [env, setEnv] = useState<LinkEnvironment>('production');
  const [busy, setBusy] = useState<string | null>(null);
  const notice = useNotice();
  const tools = useMemo(() => (data ?? []).filter((t) => t.environment === env), [data, env]);

  const open = async (tool: LinkedTool) => {
    setBusy(tool.id);
    try {
      await openTool(tool, api);
    } catch (e) {
      notice.error(`Could not open ${tool.name}: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Page
      title="Linked tools"
      description="Other nyce.logic administration tools. SSO tools open already signed in."
      actions={<Tabs value={env} onChange={setEnv} options={ENVIRONMENTS} />}
    >
      <ErrorBox error={error} />
      <Loading loading={loading && !data} />
      {data && !tools.length && (
        <EmptyState card title={`No ${env} tools configured`} hint="Links are managed by hub admins." />
      )}
      {!!tools.length && (
        <div className="oph-tool-grid">
          {tools.map((t) => (
            <ToolCard key={t.id} tool={t} busy={busy === t.id} onOpen={() => void open(t)} />
          ))}
        </div>
      )}
      {notice.element}
    </Page>
  );
}
